'use client'

import { useEffect, useRef, useState } from 'react'

import type { About } from '@/payload-types'

import { useLocale } from '../LocaleContext'

export type ExperienceStats = About['experienceStats']

/**
 * Red statistics band (Years of Experience, Varieties of Filters, headline).
 * Values come from Payload About global → experienceStats; count-up on viewport enter.
 */
const DURATION_MS = 1800

const DEFAULT_COUNTS = [
  { value: '25+', label: 'Years of Experience' },
  { value: '3K+', label: 'Varieties of Filters' },
] as const

function parseStatValue(raw: string): { end: number; format: (n: number) => string } {
  const trimmed = raw.trim()
  const match = trimmed.match(/^([\d,]+)(K\+|k\+|\+|K|k)?$/i)

  if (!match) {
    return { end: 0, format: () => trimmed }
  }

  const num = Number.parseInt(match[1].replace(/,/g, ''), 10)
  const suffix = (match[2] ?? '').toUpperCase()

  if (suffix === 'K+' || suffix === 'K') {
    return { end: num, format: (n) => `${n}K+` }
  }

  if (suffix === '+') {
    return { end: num, format: (n) => `${n}+` }
  }

  return { end: num, format: (n) => String(n) }
}

function useCountUp(end: number, inView: boolean, suffix: string, format?: (n: number) => string) {
  const [value, setValue] = useState(0)
  const startRef = useRef<number | null>(null)
  const rafRef = useRef<number>(0)

  useEffect(() => {
    if (!inView) return
    startRef.current = performance.now()

    const tick = (now: number) => {
      const start = startRef.current ?? now
      const elapsed = now - start
      const t = Math.min(elapsed / DURATION_MS, 1)
      const eased = 1 - (1 - t) ** 2
      const current = Math.round(eased * end)
      setValue(current)
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [end, inView])

  const display = format ? format(value) : `${value}${suffix}`
  return display
}

type RedStatisticsBandProps = {
  experienceStats?: ExperienceStats | null
}

export function RedStatisticsBand({ experienceStats }: RedStatisticsBandProps) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)
  const { locale, translatedData } = useLocale()

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setInView(true)
      },
      { threshold: 0.25, rootMargin: '0px 0px -40px 0px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const cmsCounts = experienceStats?.counts?.length ? experienceStats.counts : DEFAULT_COUNTS
  const firstStat = parseStatValue(cmsCounts[0]?.value ?? DEFAULT_COUNTS[0].value)
  const secondStat = parseStatValue(cmsCounts[1]?.value ?? DEFAULT_COUNTS[1].value)

  const firstLabel =
    cmsCounts[0]?.label?.trim() || translatedData?.common.yoe || DEFAULT_COUNTS[0].label
  const secondLabel =
    cmsCounts[1]?.label?.trim() || translatedData?.common.varieties || DEFAULT_COUNTS[1].label
  const headline =
    experienceStats?.headline?.trim() ||
    translatedData?.common.explore_our_filters ||
    'EXPLORE OUR WIDE RANGE OF FILTERS'

  const firstCount = useCountUp(firstStat.end, inView, '', firstStat.format)
  const secondCount = useCountUp(secondStat.end, inView, '', secondStat.format)

  return (
    <div
      ref={sectionRef}
      className="relative flex min-h-[120px] overflow-hidden px-4 py-10 text-white max-[400px]:px-3 md:flex-wrap md:items-center md:justify-center md:gap-6 md:py-10 md:px-10 lg:px-[141px]"
      style={{
        backgroundImage: 'url(/images/footer.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(200, 16, 46, 0.94)' }}
        aria-hidden
      />
      {/* Mobile: vertical stack, centered. Desktop: horizontal row, centered */}
      <div className="relative z-10 flex w-full flex-col items-center gap-8 text-center md:flex-row md:flex-wrap md:justify-center md:gap-6 md:gap-x-8 lg:gap-8">
        <div className="flex min-w-0 flex-shrink-0 items-center justify-center gap-3 md:justify-start md:text-left">
          <p className="text-4xl font-bold md:text-5xl" aria-live="polite">
            {firstCount}
          </p>
          <div
            className="min-h-[2.5rem] w-0 flex-shrink-0 self-stretch"
            style={{ borderLeft: '2px solid rgba(255,255,255,0.35)' }}
            aria-hidden
          />
          <p className="font-poppins text-sm font-extrabold uppercase tracking-wider opacity-95 break-words w-28">
            {firstLabel}
          </p>
        </div>
        <div className="flex min-w-0 flex-shrink-0 items-center justify-center gap-3 md:justify-start md:text-left">
          <p className="text-4xl font-bold md:text-5xl" aria-live="polite">
            {secondCount}
          </p>
          <div
            className="min-h-[2.5rem] w-0 flex-shrink-0 self-stretch"
            style={{ borderLeft: '2px solid rgba(255,255,255,0.35)' }}
            aria-hidden
          />
          <p className="font-poppins text-sm font-extrabold uppercase tracking-wider opacity-95 break-words w-28">
            {secondLabel}
          </p>
        </div>
        <div
          dir={locale == 'ar' ? 'rtl' : 'ltr'}
          className="flex min-w-0 flex-shrink-0 items-center justify-center gap-2 md:mt-0 md:justify-start "
        >
          <p
            className={`font-bebas-neue max-w-md text-center text-lg font-bold uppercase leading-snug md:text-left rtl:md:text-right md:text-xl tracking-[1px] break-words w-36 `}
          >
            {headline}
          </p>
        </div>
      </div>
    </div>
  )
}
